import {
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import styled, { css } from "styled-components";

import { ClusterIcon, LocationArrowIcon, ScreenFullIcon } from "../../icons/glyphs";
import { isRenderableNode } from "../../utils/reactNode";
import { IconButton } from "../IconButton";
import { Menu } from "../Menu";
import { NotificationBadge, type NotificationBadgeTone } from "../NotificationBadge";
import { Tooltip, useTooltipController } from "../Tooltip";
import { useVideoTimeline, type PlaybackTransport } from "../Video";
import { controlTransition } from "../foundations/controlStyles";
import { SrOnly } from "../foundations/srOnly";
import {
  findNearestMediaTimelinePoint,
  formatMediaPlaybackTime,
  MEDIA_TIMELINE_POLICY,
  normalizeMediaTimelineCurrent,
} from "./mediaTimelineMath";
import type {
  MediaTimelineMarker,
  NormalizedMarker,
  TimelineFocusOrigin,
  VisibleMarker,
} from "./timelineTypes";

const TIMELINE_DURATION_EPSILON_SECONDS = MEDIA_TIMELINE_POLICY.durationComparisonEpsilonSeconds;

export const MarkerRail = styled.div`
  position: relative;
  min-width: 0;
  margin-top: 0.35rem;
`;

export const MarkerRailTrack = styled.div`
  position: relative;
  min-width: 0;
  height: 1.75rem;
`;

const markerAnchorStyles = css<{ $active: boolean }>`
  position: absolute;
  z-index: 3;
  top: 0;
  left: var(--c6-media-marker-position);
  display: inline-flex;
  width: 1.55rem;
  height: 1.75rem;
  justify-content: center;
  transform: translateX(-50%);

  &::after {
    position: absolute;
    bottom: -0.15rem;
    left: 50%;
    width: 0.72rem;
    height: 0.15rem;
    border-radius: 999px;
    background: ${({ theme }) => theme.color.border.selected};
    content: "";
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transform: translateX(-50%);
    ${controlTransition("opacity")}
  }
`;

const MarkerTooltip = styled(Tooltip)<{ $active: boolean }>`
  ${markerAnchorStyles}
`;

const MarkerClusterTooltip = styled(Tooltip)<{ $active: boolean }>`
  ${markerAnchorStyles}
  width: 1.75rem;
`;

const MarkerButton = styled(IconButton)`
  position: absolute;
  top: 0.25rem;
  left: 50%;
  transform: translateX(-50%);

  &::before {
    position: absolute;
    top: -0.25rem;
    left: calc(50% - 1px);
    width: 2px;
    height: 0.25rem;
    border-radius: 999px;
    background: currentColor;
    content: "";
  }
`;

const MarkerIconSurface = styled.span`
  display: inline-flex;
  width: 1rem;
  height: 1rem;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const MarkerClusterCount = styled.span`
  display: inline-flex;
  height: 1rem;
  align-items: center;
  justify-content: center;
  gap: 0.0625rem;
`;

const MarkerClusterGlyph = styled(ClusterIcon)`
  width: 0.75rem;
  height: 0.75rem;
  flex: 0 0 auto;
`;

const MarkerClusterCountValue = styled.span`
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.action};
  font-variant-numeric: tabular-nums;
  line-height: 1;
`;

function MarkerIcon({ marker }: { marker: MediaTimelineMarker }) {
  // One dot with a severity pick; the accessible name lists every signal, so a lower-severity
  // one is announced even when not drawn.
  const badgeTone = resolveMarkerBadgeTone([marker]);
  return (
    <NotificationBadge
      visible={badgeTone !== null}
      tone={badgeTone ?? "error"}
      size="0.45rem"
      offset="-0.16rem"
    >
      <MarkerIconSurface aria-hidden="true" data-media-marker-badge={badgeTone ?? undefined}>
        {/* 기본 마커는 frame viewport 브래킷 — 전용 FrameIcon은 ScreenFullIcon과 실루엣이
            동일해 하나로 수렴했다(fullscreen 동작이 아니라 브래킷 실루엣을 빌리는 자리). */}
        {marker.icon ?? <ScreenFullIcon data-media-marker-icon="frame" />}
      </MarkerIconSurface>
    </NotificationBadge>
  );
}

/**
 * The icon seat and badge dot are aria-hidden visuals, so a marker's signals reach assistive
 * technology only through the accessible name; each consumer badge announces its own label.
 */
function resolveMarkerSuffixes(marker: MediaTimelineMarker): string[] {
  return marker.badge === undefined ? [] : [marker.badge.label];
}

/**
 * One dot per marker (or cluster): the drawn tone is the most severe candidate. Severity is
 * presentation policy over c6's own tone union — the meaning of each signal stays consumer-owned.
 */
// 공개 tone마다 순위를 명시해 tone 유니온이 확장될 때 표현 정책의 누락을 타입으로 막는다.
const BADGE_TONE_SEVERITY: Readonly<Record<NotificationBadgeTone, number>> = {
  error: 0,
  cancelling: 1,
  recording: 2,
  warning: 3,
  attention: 4,
  exhausted: 5,
  running: 6,
  checking: 7,
  info: 8,
  success: 9,
  completed: 10,
  ready: 11,
  paused: 12,
  muted: 13,
};

function resolveMarkerBadgeTone(
  markers: readonly MediaTimelineMarker[],
): NotificationBadgeTone | null {
  const candidates = markers.flatMap<NotificationBadgeTone>((marker) =>
    marker.badge === undefined ? [] : [marker.badge.tone],
  );
  if (candidates.length === 0) return null;
  return [...candidates].sort(
    (left, right) => BADGE_TONE_SEVERITY[left] - BADGE_TONE_SEVERITY[right],
  )[0];
}

function joinMarkerSuffixes(base: string, suffixes: readonly string[]): string {
  return suffixes.length === 0 ? base : `${base} · ${suffixes.join(" · ")}`;
}

interface SingleMarkerProps {
  entry: VisibleMarker;
  /** The media duration: marker times take its clock form. */
  durationSeconds: number;
  position: string;
  disabled: boolean;
  fallbackLabel: string;
  onSelect: (markerId: string) => void;
  onFocusTimeline: (origin: TimelineFocusOrigin) => void;
}

export function SingleMarker({
  entry,
  durationSeconds,
  position,
  disabled,
  fallbackLabel,
  onSelect,
  onFocusTimeline,
}: SingleMarkerProps) {
  const { marker, timeSeconds } = entry;
  const formattedTime = formatMediaPlaybackTime(timeSeconds, durationSeconds);
  const actionLabel = joinMarkerSuffixes(
    `${marker.label ?? fallbackLabel}: ${formattedTime}`,
    resolveMarkerSuffixes(marker),
  );
  const markerStyle = { "--c6-media-marker-position": position } as CSSProperties;

  return (
    <MarkerTooltip
      $active={marker.active === true}
      // Renderable-check, not `??`: a conditional consumer slot can hand `false`/`""`, and
      // non-renderable content makes Tooltip drop its wrapper — the element that owns this
      // marker's absolute position — leaving the button at the rail's own `left: 50%`.
      content={isRenderableNode(marker.tooltipContent) ? marker.tooltipContent : actionLabel}
      placement="top"
      style={markerStyle}
    >
      <MarkerButton
        size="xs"
        variant="surface"
        border={false}
        data-testid="timeline-marker"
        data-media-marker-visual="true"
        data-active={marker.active === true ? "true" : "false"}
        aria-label={actionLabel}
        aria-current={marker.active === true ? "true" : undefined}
        disabled={disabled}
        onClick={(event) => {
          onSelect(marker.id);
          // A marker stays enabled after selection, so keyboard focus belongs on it.
          if (event.detail > 0) onFocusTimeline("pointer");
        }}
      >
        <MarkerIcon marker={marker} />
      </MarkerButton>
    </MarkerTooltip>
  );
}

interface MarkerClusterProps {
  entries: readonly VisibleMarker[];
  /** The media duration: marker times take its clock form. */
  durationSeconds: number;
  position: string;
  disabled: boolean;
  fallbackLabel: string;
  groupLabel: string;
  onSelect: (markerId: string) => void;
  onFocusTimeline: (origin: TimelineFocusOrigin) => void;
}

export function MarkerCluster({
  entries,
  durationSeconds,
  position,
  disabled,
  fallbackLabel,
  groupLabel,
  onSelect,
  onFocusTimeline,
}: MarkerClusterProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const timelineFocusPendingRef = useRef<TimelineFocusOrigin | null>(null);
  const tooltip = useTooltipController({ suppressed: menuOpen });
  const active = entries.some(({ marker }) => marker.active === true);
  const clusterBadgeTone = resolveMarkerBadgeTone(entries.map(({ marker }) => marker));
  const firstTime = entries[0]?.timeSeconds ?? 0;
  const lastTime = entries.at(-1)?.timeSeconds ?? firstTime;
  const timeRange =
    firstTime === lastTime
      ? formatMediaPlaybackTime(firstTime, durationSeconds)
      : `${formatMediaPlaybackTime(firstTime, durationSeconds)}–${formatMediaPlaybackTime(lastTime, durationSeconds)}`;
  const clusterLabel = joinMarkerSuffixes(`${entries.length} ${groupLabel}: ${timeRange}`, [
    ...new Set(
      entries.flatMap(({ marker }) => (marker.badge === undefined ? [] : [marker.badge.label])),
    ),
  ]);
  const markerStyle = { "--c6-media-marker-position": position } as CSSProperties;
  const handleMenuOpenChange = useCallback(
    (open: boolean) => {
      setMenuOpen(open);
      const focusOrigin = timelineFocusPendingRef.current;
      if (!open && focusOrigin !== null) {
        timelineFocusPendingRef.current = null;
        window.requestAnimationFrame(() => onFocusTimeline(focusOrigin));
      }
    },
    [onFocusTimeline],
  );

  // Disabling the bar mid-interaction releases the popup the way drag/edge-pan state is
  // released (and the dropdowns close) — an open menu would otherwise keep a keyboard focus
  // scope attached to a disabled timeline.
  useLayoutEffect(() => {
    if (!disabled) return;
    timelineFocusPendingRef.current = null;
    setMenuOpen(false);
  }, [disabled]);

  return (
    <MarkerClusterTooltip
      $active={active}
      content={clusterLabel}
      placement="top"
      controller={tooltip}
      describeTrigger={false}
      style={markerStyle}
    >
      <Menu
        label={clusterLabel}
        placement="top"
        open={menuOpen}
        onOpenChange={handleMenuOpenChange}
      >
        <Menu.Trigger>
          <MarkerButton
            {...tooltip.triggerProps}
            size="sm"
            variant="surface"
            border={false}
            data-testid="timeline-marker-cluster"
            data-media-marker-cluster="true"
            data-active={active ? "true" : "false"}
            aria-label={clusterLabel}
            aria-current={active ? "true" : undefined}
            disabled={disabled}
          >
            <NotificationBadge
              visible={clusterBadgeTone !== null}
              tone={clusterBadgeTone ?? "error"}
              size="0.45rem"
              offset="-0.28rem"
            >
              <MarkerClusterCount
                aria-hidden="true"
                data-media-marker-badge={clusterBadgeTone ?? undefined}
              >
                <MarkerClusterGlyph data-media-marker-cluster-icon="true" />
                {/* Capped so the widest possible count still fits the fixed-width anchor the
                    cluster distance is sized for — an uncapped count widens the trigger into
                    the separation guarantee. Exact counts stay in the label and menu. */}
                <MarkerClusterCountValue>
                  {entries.length > 9 ? "9+" : entries.length}
                </MarkerClusterCountValue>
              </MarkerClusterCount>
            </NotificationBadge>
          </MarkerButton>
        </Menu.Trigger>
        {entries.map(({ marker, timeSeconds }) => (
          <Menu.Item
            key={marker.id}
            icon={<MarkerIcon marker={marker} />}
            caption={formatMediaPlaybackTime(timeSeconds, durationSeconds)}
            disabled={disabled}
            onSelect={(event) => {
              timelineFocusPendingRef.current = event.detail > 0 ? "pointer" : "keyboard";
              onSelect(marker.id);
            }}
          >
            {marker.label ?? fallbackLabel}
            {resolveMarkerSuffixes(marker).map((suffix) => (
              <SrOnly key={suffix}> · {suffix}</SrOnly>
            ))}
          </Menu.Item>
        ))}
      </Menu>
    </MarkerClusterTooltip>
  );
}

interface NearestMarkerSelection {
  point?: NormalizedMarker;
  atPoint: boolean;
}

interface NearestMarkerControlProps {
  transport: PlaybackTransport;
  durationSeconds: number;
  points: readonly NormalizedMarker[];
  disabled: boolean;
  actionLabel: string;
  onSelect: (point: NormalizedMarker) => void;
  onFocusTimeline: (origin: TimelineFocusOrigin) => void;
}

export const NearestMarkerControl = memo(function NearestMarkerControl({
  transport,
  durationSeconds,
  points,
  disabled,
  actionLabel,
  onSelect,
  onFocusTimeline,
}: NearestMarkerControlProps) {
  const selection = useVideoTimeline<NearestMarkerSelection>(
    transport,
    (snapshot) => {
      const current = normalizeMediaTimelineCurrent(snapshot.currentTimeSeconds, durationSeconds);
      const point = findNearestMediaTimelinePoint(points, current);
      return {
        point,
        atPoint:
          point !== undefined &&
          Math.abs(current - point.timeSeconds) <= TIMELINE_DURATION_EPSILON_SECONDS,
      };
    },
    (left, right) => left.point === right.point && left.atPoint === right.atPoint,
  );
  const tooltip = useTooltipController();
  const { point } = selection;
  if (!point) return null;

  const { marker, timeSeconds } = point;
  const label = `${actionLabel}: ${formatMediaPlaybackTime(timeSeconds, durationSeconds)}`;
  let tooltipContent: ReactNode = label;
  if (isRenderableNode(marker.tooltipContent)) {
    tooltipContent = (
      <>
        {actionLabel} · {marker.tooltipContent}
      </>
    );
  } else if (marker.label) {
    tooltipContent = `${actionLabel} · ${marker.label} · ${formatMediaPlaybackTime(timeSeconds, durationSeconds)}`;
  }

  return (
    <Tooltip
      content={tooltipContent}
      placement="top"
      controller={tooltip}
      describeTrigger={false}
      {...tooltip.triggerProps}
    >
      <IconButton
        size="md"
        variant="surface"
        aria-label={label}
        disabled={disabled || selection.atPoint}
        data-media-nearest-marker-control="true"
        onClick={(event) => {
          tooltip.hide();
          onSelect(point);
          // This control disables itself once the playhead reaches the frame, so keyboard focus
          // has to move on with it — the cluster menu hands focus over the same way.
          onFocusTimeline(event.detail > 0 ? "pointer" : "keyboard");
        }}
      >
        <LocationArrowIcon />
      </IconButton>
    </Tooltip>
  );
});
